import React, { useState } from 'react';
import { Button, Col, Popover, Table, Tag } from 'components';
import { PurchaseOrderItem, PurchaseOrderItemsTableProps } from './types';
import { ADMIN_ROUTES } from 'constants/routes';
import { Link } from 'react-router-dom';
import { OrderItemStatusBadge } from 'components/common/StatusBadge';
import { LoadingOutlined } from '@ant-design/icons';
import { TotalDocumentsIcon } from 'components/common/icons/purchaseRequest/TotalDocumentsIcon';
import styles from './styles.module.scss';
import { SalesPriceDrawer } from 'feature/purchaseOrderTabs/general/salesPriceDrawer';
import { ItemAttachmentsModal } from './ItemAttachmentsModal';
import { Row, Typography } from 'antd';

const { Text } = Typography;

export const PurchaseOrderItemsTable: React.FC<PurchaseOrderItemsTableProps> = ({
  data,
  loading = false,
  pagination,
  onTableChange,
}) => {
  const [itemIds, setItemIds] = useState<string[]>();
  const [salesPriceDrawerState, setSalesPriceDrawerState] = useState<boolean>(false);
  const [attachmentsModalVisible, setAttachmentsModalVisible] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<any[]>([]);

  const onSalesPriceDrawerExpand = (values?: string[]) => {
    setItemIds(values);
    setSalesPriceDrawerState((oState) => !oState);
  };

  const handleOpenAttachments = (attachments: any[]) => {
    setSelectedAttachments(attachments || []);
    setAttachmentsModalVisible(true);
  };

  const handleCloseAttachments = () => {
    setAttachmentsModalVisible(false);
    setSelectedAttachments([]);
  };

  const calculateMarginRange = (marginMin: number | null | undefined, marginMax: number | null | undefined) => {
    if ((marginMin === null || marginMin === undefined) && (marginMax === null || marginMax === undefined)) return '-';
    if (marginMin !== null && marginMin !== undefined && marginMax !== null && marginMax !== undefined) {
      return `${Math.round(marginMin)}%-${Math.round(marginMax)}%`;
    }
    if (marginMin !== null && marginMin !== undefined) return `${Math.round(marginMin)}%`;
    if (marginMax !== null && marginMax !== undefined) return `${Math.round(marginMax)}%`;
    return '-';
  };

  const renderCommercialOffers = (offers: any[] | null) => {
    if (!offers || offers.length === 0) return '-';

    if (offers.length === 1) {
      return (
        <Link
          to={`${ADMIN_ROUTES.deals.view}/${offers[0].dealId}/${ADMIN_ROUTES.deals.commercialOfferUpdate}/${offers[0].id}`}
          target="_blank"
        >
          <Text type={'secondary'} underline>
            {offers[0].uuid}
          </Text>
        </Link>
      );
    }

    const firstOffer = offers[0];
    const remainingCount = offers.length - 1;

    return (
      <Row justify={'space-between'}>
        <Col>
          <Link
            to={`${ADMIN_ROUTES.deals.view}/${firstOffer.dealId}/${ADMIN_ROUTES.deals.commercialOfferUpdate}/${firstOffer.id}`}
            target="_blank"
          >
            <Text type={'secondary'} underline>
              {firstOffer.uuid}
            </Text>
          </Link>
        </Col>
        {remainingCount > 0 && (
          <Col>
            <Popover
              content={
                <Row style={{ maxWidth: 240 }} gutter={[12, 12]}>
                  {offers.slice(1).map((offer: any) => (
                    <Col span={8} key={offer.id}>
                      <Link
                        to={`${ADMIN_ROUTES.deals.view}/${offer.dealId}/${ADMIN_ROUTES.deals.commercialOfferUpdate}/${offer.id}`}
                        target="_blank"
                      >
                        {offer.uuid}
                      </Link>
                    </Col>
                  ))}
                </Row>
              }
              title="Commercial Offers"
              trigger="click"
            >
              <Tag className={styles.additionalOffersTag}>+{remainingCount}</Tag>
            </Popover>
          </Col>
        )}
      </Row>
    );
  };

  const columns = [
    {
      title: 'Item SKU',
      dataIndex: 'itemSku', // Changed from 'sku'
      key: 'itemSku',
      sorter: true,
      width: 150,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'itemDescription',
      sorter: true,
      width: 200,
      ellipsis: true,
      render: (desc: any) => {
        if (!desc) return '-';
        return (
          <Text ellipsis={{ tooltip: true }} style={{ width: 200 }}>
            {desc}
          </Text>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: true,
      width: 80,
      align: 'center' as const,
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      width: 200,
      render: (brand: any) => {
        if (!brand) return '-';
        return (
          <Text ellipsis={{ tooltip: true }} style={{ width: 200 }}>
            <Link to={`${ADMIN_ROUTES.brand?.view || '/admin/brands'}/${brand.id}/management`} target="_blank">
              {brand.name}
            </Link>
          </Text>
        );
      },
    },
    {
      title: 'Purchase Price',
      key: 'purchasePriceFrom',
      sorter: true,
      width: 120,
      align: 'right' as const,
      render: (record: PurchaseOrderItem) => {
        const { purchasePriceFrom, purchasePriceTo } = record;
        if (purchasePriceFrom === null && purchasePriceTo === null) return '-';

        const priceLabel =
          purchasePriceFrom && purchasePriceTo
            ? `€${purchasePriceFrom}-${purchasePriceTo}`
            : `€${purchasePriceFrom ?? purchasePriceTo}`;
        return <Text type={'secondary'}>{priceLabel}</Text>;
      },
    },
    {
      title: 'Sales Price',
      key: 'minSalesPrice',
      sorter: true,
      width: 150,
      align: 'right' as const,
      render: (record: PurchaseOrderItem) => {
        const { salesPriceMin, salesPriceMax, ids } = record;
        if (salesPriceMin === null && salesPriceMax === null) return '-';

        const priceLabel =
          salesPriceMin && salesPriceMax ? `€${salesPriceMin}-${salesPriceMax}` : `€${salesPriceMin ?? salesPriceMax}`;
        return (
          <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => onSalesPriceDrawerExpand(ids)}>
            <Text type={'secondary'} underline>
              {priceLabel}
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Margin Range',
      key: 'minMargin',
      sorter: true,
      width: 120,
      align: 'center' as const,
      render: (record: PurchaseOrderItem) => calculateMarginRange(record.marginMin, record.marginMax),
    },
    {
      title: 'Commercial Offers',
      dataIndex: 'commercialOffers',
      key: 'commercialOffers',
      width: 180,
      render: renderCommercialOffers,
    },
    {
      title: 'Attch.',
      dataIndex: 'attachments',
      key: 'attachments',
      width: 80,
      align: 'center' as const,
      render: (attachments: any[]) => {
        const count = attachments?.length || 0;
        if (count === 0) return '-';

        return (
          <Button type="link" onClick={() => handleOpenAttachments(attachments)}>
            <TotalDocumentsIcon />
            <Text type={'secondary'} underline>
              <Text type="secondary">{count}</Text>
            </Text>
          </Button>
        );
      },
    },
    {
      title: 'Item Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: string) => {
        if (!status) return '-';
        return <OrderItemStatusBadge type={status} />;
      },
    },
  ];

  return (
    <div className={styles.itemsTableContainer}>
      <Table
        tableLayout="auto"
        bordered
        scroll={{ x: true }}
        rowKey="ids"
        size="small"
        dataSource={data}
        columns={columns}
        loading={{
          indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
          spinning: loading,
        }}
        locale={
          loading
            ? { emptyText: null } // Hide "No Data" text while loading
            : {} // Use default AntD "No Data" icon when empty
        }
        onChange={onTableChange}
        pagination={{
          position: ['bottomCenter'],
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
          size: 'default',
          current: pagination.page,
          pageSize: pagination.size,
          total: pagination.total,
        }}
      />
      <SalesPriceDrawer drawerState={salesPriceDrawerState} onExpand={onSalesPriceDrawerExpand} data={itemIds} />
      <ItemAttachmentsModal
        visible={attachmentsModalVisible}
        attachments={selectedAttachments}
        onClose={handleCloseAttachments}
      />
    </div>
  );
};
